import { useEffect, useState, useRef, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { DataGrid } from "@mui/x-data-grid"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box
} from "@mui/material"
import { DataGridStyles, DataGridWrapperStyles, DialogStyles } from "../styles/MaintenanceStyles"
import {
    getTools,
    updateTool,
    updateselected,
    updateIsBeingEdited,
    setAlertDialog,
    setAlertText
} from "../../../actions/maintenanceActions"
import {
    toColumns,
    computeInspectionMutation,
    useToolMutation
} from "../MaintenanceSharedFunctions"
import ToolToolBar from "./ToolToolBar"

export default function Tool() {
    const dispatch = useDispatch()
    const mutateRow = useToolMutation()
    const noButtonRef = useRef(null)

    // Store Variables
    const maintenance = useSelector((state) => state.maintenance)
    const auth = useSelector((state) => state.auth)
    const permissions = useSelector((state) => state.auth.permissions)

    const [promiseArguments, setPromiseArguments] = useState(null)

    useEffect(() => {
        if (auth.authenticated) {
            async function runUseEffectFunctions() {
                await dispatch(getTools())
            }
            runUseEffectFunctions()
        }
    }, [dispatch, auth])

    //creating columns and rows for the grid
    let columns = []
    let rows = []
    const Tools = maintenance.tools
    if (Tools?.length > 0) {
        columns = toColumns(Object.keys(Tools[0]), permissions)
        rows = Tools
    }

    //handling the grid's row update
    const processRowUpdate = useCallback(
        (newRow, oldRow) =>
            new Promise((resolve, reject) => {
                const mutation = computeInspectionMutation(newRow, oldRow)
                if (mutation) {
                    // Save the arguments to resolve or reject the promise later
                    setPromiseArguments({ resolve, reject, newRow, oldRow })
                } else {
                    resolve(oldRow) // Nothing was changed
                }
            }),
        []
    )

    //handling error in row update
    const onProcessRowUpdateError = () => {
        if (promiseArguments) {
            promiseArguments.reject()
            setPromiseArguments(null)
        }
    }

    //handle closing confirmation dialog on no
    const handleCancelUpdateTool = () => {
        const { oldRow, resolve } = promiseArguments
        resolve(oldRow) // Resolve with the old row to not update the internal state
        setPromiseArguments(null)
        dispatch(setAlertDialog(false))
        dispatch(updateselected([]))
        dispatch(updateIsBeingEdited(false))
    }

    //handle closing confirmation dialog on yes
    const handleConfirmUpdateTool = async () => {
        const { newRow, oldRow, reject, resolve } = promiseArguments
        if (newRow.Tool.trim() !== "") {
            if (
                maintenance.tools?.filter(
                    (Tool) =>
                        Tool.Tool.toLowerCase() === newRow.Tool.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This inspection method already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    // Make the HTTP request to save in the backend
                    const response = await mutateRow(newRow)
                    const newTool = {
                        Tool: response,
                        AuditLog: {
                            ToolId: response.ToolId,
                            Action: "Update Inspection Method",
                            OldValue: oldRow.Tool,
                            NewValue: newRow.Tool,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    // Update inspection method and refresh store data
                    await dispatch(updateTool(newTool))
                    await dispatch(getTools())
                    await dispatch(updateselected([]))
                    await dispatch(updateIsBeingEdited(false))
                    // Resolve with the new row to update the internal state
                    resolve(response)
                    // Clear the promise arguments to avoid resolving or rejecting the same promise twice
                    setPromiseArguments(null)
                } catch (error) {
                    reject(oldRow)
                    console.log(error)
                    setPromiseArguments(null)
                    await dispatch(updateselected([]))
                    await dispatch(updateIsBeingEdited(false))
                }
            }
        } else {
            reject(oldRow)
            setPromiseArguments(null)
        }
    }

    //update store when clicking on the row
    const handleRowClick = (event, row) => {
        dispatch(updateselected(event.row))
    }

    const handleRowEdit = async (row) => {
        await dispatch(updateIsBeingEdited(true))
    }

    const handleRowEditStop = async (row) => {
        await dispatch(updateIsBeingEdited(false))
    }

    const handleEntered = () => {
        // The `autoFocus` is not used because, if used, the same Enter that saves
        // the cell triggers "No". Instead, we manually focus the "No" button once
        // the dialog is fully open.
        // noButtonRef.current?.focus();
    }

    // Handles rendering the dialog to confirm updating a inspection method
    const renderConfirmUpdateToolDialog = () => {
        if (!promiseArguments) {
            return null
        }

        const { newRow, oldRow } = promiseArguments
        const mutation = computeInspectionMutation(newRow, oldRow)

        return (
            <Dialog
                sx={DialogStyles}
                maxWidth="xs"
                TransitionProps={{ onEntered: handleEntered }}
                open={!!promiseArguments}
            >
                <DialogTitle sx={DialogStyles}>
                    Are you sure?
                </DialogTitle>
                <DialogContent dividers>
                    {`Pressing 'Yes' will change ${mutation}.`}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmUpdateTool}
                        sx={DialogStyles}
                    >
                        Yes
                    </Button>
                    <Button
                        ref={noButtonRef}
                        onClick={handleCancelUpdateTool}
                        sx={DialogStyles}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Box sx={DataGridWrapperStyles}>
            {renderConfirmUpdateToolDialog()}
            <DataGrid
                getRowId={(row) => row.ToolId}
                rows={rows}
                columns={columns}
                columnVisibilityModel={{
                    ToolId: false
                }}
                processRowUpdate={processRowUpdate}
                onRowClick={handleRowClick}
                onProcessRowUpdateError={onProcessRowUpdateError}
                experimentalFeatures={{ newEditingApi: true }}
                components={{ Toolbar: ToolToolBar }}
                sx={DataGridStyles}
                onCellEditStart={handleRowEdit}
                onCellEditStop={handleRowEditStop}
            />
        </Box>
    )
}
